/* eslint-disable no-console */
import { db, webApi, auth, timestamp } from "@/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  writeBatch,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  limit,
  startAfter
} from "firebase/firestore";
import axios from "axios";
import * as utils from "@/utils";
import _ from "lodash";
import { AVATAR_COLORS } from "@/constants/colors";
import { signInAnonymously } from "firebase/auth";
import encryptionService from "@/services/encryptionService";

const state = {
  members: [],
  loadingMembers: false,
  currentMemberPoints: {},
  currentMember: null,
  loadingCurrentMember: false,
  error: null,
  importProgress: {
    importing: false,
    progress: 0,
    total: 0,
    processed: 0
  },
  memberDetails: {},
  lastVisible: null,
  hasMore: true
};

// Helper functions for member data encryption/decryption
const encryptMemberSensitiveData = memberData => {
  if (!memberData) return memberData;

  const fieldsToEncrypt = encryptionService.getEncryptionFields("member");
  return encryptionService.encryptObject(memberData, fieldsToEncrypt);
};

const decryptMemberSensitiveData = memberData => {
  if (!memberData) return memberData;

  const fieldsToDecrypt = encryptionService.getEncryptionFields("member");
  return encryptionService.decryptObject(memberData, fieldsToDecrypt);
};

const actions = {
  async loadMembers(
    { commit, getters },
    { pageSize = 50, reset = false } = {}
  ) {
    if (reset) {
      commit("setLastVisible", null);
      commit("setHasMore", true);
    }

    if (!getters.hasMore && !reset) {
      return [];
    }

    commit("setLoadingMembers", true);
    commit("setError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const membersRef = collection(programRef, "members");

      let membersQuery = query(
        membersRef,
        orderBy("fullnameUppercase"),
        limit(pageSize)
      );

      if (getters.lastVisible && !reset) {
        membersQuery = query(
          membersRef,
          orderBy("fullnameUppercase"),
          startAfter(getters.lastVisible),
          limit(pageSize)
        );
      }

      const querySnapshot = await getDocs(membersQuery);

      if (querySnapshot.empty) {
        commit("setHasMore", false);
        return [];
      }

      const members = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        const decryptedData = decryptMemberSensitiveData(data);

        members.push({
          id: docSnapshot.id,
          ...decryptedData,
          fullname: `${decryptedData.firstname ||
            ""} ${decryptedData.lastname || ""}`.trim(),
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        });
      });

      if (reset) {
        commit("setMembers", members);
      } else {
        commit("addMembers", members);
      }

      commit(
        "setLastVisible",
        querySnapshot.docs[querySnapshot.docs.length - 1]
      );
      commit("setHasMore", querySnapshot.docs.length === pageSize);

      return members;
    } catch (error) {
      console.error("Error loading members:", error);
      commit("setError", error.message);
      commit("setMembers", []);
      throw error;
    } finally {
      commit("setLoadingMembers", false);
    }
  },

  async getMemberById({ commit, getters }, memberId) {
    if (!memberId) {
      console.error("Member ID is required");
      return null;
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const memberRef = doc(collection(programRef, "members"), memberId);
      const memberDoc = await getDoc(memberRef);

      if (memberDoc.exists()) {
        const data = memberDoc.data();
        const decryptedData = decryptMemberSensitiveData(data);

        return {
          id: memberDoc.id,
          ...decryptedData,
          fullname: `${decryptedData.firstname ||
            ""} ${decryptedData.lastname || ""}`.trim(),
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        };
      } else {
        console.warn(`Member with ID ${memberId} not found`);
        return null;
      }
    } catch (error) {
      console.error("Error getting member by ID:", error);
      commit("setError", error.message);
      return null;
    }
  },

  async loadCurrentMember({ commit, getters }, memberId) {
    if (!memberId) {
      throw new Error("Member ID is required");
    }

    commit("setLoadingCurrentMember", true);
    commit("setCurrentMember", null);
    commit("setError", null);

    try {
      const member = await this.dispatch("getMemberById", memberId);
      if (member) {
        commit("setCurrentMember", member);
        return member;
      } else {
        throw new Error("Member not found");
      }
    } catch (error) {
      console.error("Error loading current member:", error);
      commit("setError", error.message);
      throw error;
    } finally {
      commit("setLoadingCurrentMember", false);
    }
  },

  async createMember({ dispatch, commit, getters }, payload) {
    console.log("Creating member:", payload);
    if (!payload || !payload.firstname || !payload.lastname || !payload.email) {
      throw new Error(
        "Member data with firstname, lastname, and email is required"
      );
    }

    commit("setError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const membersRef = collection(programRef, "members");

      // Check for duplicate email (both encrypted and legacy data)
      let emailDupSnapshot = { empty: true };

      // Try encrypted email first
      try {
        const encryptedEmail = encryptionService.encrypt(payload.email);
        const encryptedEmailQuery = query(
          membersRef,
          where("email", "==", encryptedEmail)
        );
        emailDupSnapshot = await getDocs(encryptedEmailQuery);
      } catch (error) {
        console.warn("Failed to check encrypted email duplicates:", error);
      }

      // If not found with encrypted email, try plain text (legacy data)
      if (emailDupSnapshot.empty) {
        const plainEmailQuery = query(
          membersRef,
          where("email", "==", payload.email)
        );
        emailDupSnapshot = await getDocs(plainEmailQuery);
      }

      if (!emailDupSnapshot.empty) {
        throw new Error("Email is already registered");
      }

      // Check for duplicate account key if provided (both encrypted and legacy data)
      if (payload.accountKey) {
        let accountKeyDupSnapshot = { empty: true };

        // Try encrypted accountKey first
        try {
          const encryptedAccountKey = encryptionService.encrypt(
            payload.accountKey
          );
          const encryptedKeyQuery = query(
            membersRef,
            where("accountKey", "==", encryptedAccountKey)
          );
          accountKeyDupSnapshot = await getDocs(encryptedKeyQuery);
        } catch (error) {
          console.warn(
            "Failed to check encrypted accountKey duplicates:",
            error
          );
        }

        // If not found with encrypted accountKey, try plain text (legacy data)
        if (accountKeyDupSnapshot.empty) {
          const plainKeyQuery = query(
            membersRef,
            where("accountKey", "==", payload.accountKey)
          );
          accountKeyDupSnapshot = await getDocs(plainKeyQuery);
        }

        if (!accountKeyDupSnapshot.empty) {
          throw new Error("Account key is already registered");
        }
      }

      // eslint-disable-next-line no-unused-vars
      const { id, ...memberInput } = payload;

      const name = payload.firstname + " " + payload.lastname;
      const initials = name
        .match(/(\b\S)?/g)
        .join("")
        .match(/(^\S|\S$)?/g)
        .join("")
        .toUpperCase();
      const colors = AVATAR_COLORS;
      const random = Math.floor(Math.random() * colors.length);
      const color = colors[random];
      const status = "Awaiting Approval";

      // Single company selections are initially a string and need to be converted to an array
      // Multiple company selections are already an array
      const companies = Array.isArray(payload.companies)
        ? payload.companies
        : [payload.companies];

      // Create a new company if a new company has been added at registration
      if (
        payload.newCompanyObj &&
        Object.keys(payload.newCompanyObj).length > 0
      ) {
        console.log("Creating new company during member registration");
        const company = {
          title: payload.newCompanyObj.title,
          accountKey: payload.newCompanyObj.accountKey || "",
          titleUppercase: payload.newCompanyObj.title.toUpperCase(),
          status: "Active",
          members: [],
          tags: [],
          points: 0
        };

        try {
          const newCompany = await dispatch("createCompany", company);
          companies.push(newCompany.id);
        } catch (error) {
          console.error("Error creating new company:", error);
          throw new Error("There was an error adding a new company");
        }
      }

      const member = {
        ...memberInput,
        fullnameUppercase: `${payload.firstname} ${payload.lastname}`.toUpperCase(),
        created: serverTimestamp(),
        updated: serverTimestamp(),
        createdBy: getters.user?.id || "system",
        updatedBy: getters.user?.id || "system",
        initials: initials,
        color: color,
        status: status,
        balance: 0,
        awarded: 0,
        redeemed: 0,
        companies: companies
      };

      // Encrypt sensitive member data before storing to database
      const encryptedMember = encryptMemberSensitiveData(member);

      const newMemberRef = await addDoc(membersRef, encryptedMember);

      const companyChanges = companies.reduce((result, companyId) => {
        const change = {
          companyId,
          memberId: newMemberRef.id,
          type: "add"
        };
        return [...result, change];
      }, []);

      if (companyChanges.length > 0) {
        try {
          await dispatch("syncCompanyMembers", companyChanges);
        } catch (error) {
          console.error("Error updating company members:", error);
          throw new Error("Error occurred when updating company members");
        }
      }

      // Create temporary member for immediate UI update
      const tempMember = {
        ...member,
        id: newMemberRef.id,
        created: new Date(),
        updated: new Date()
      };

      commit("createMember", tempMember);
      dispatch(
        "setSnackbar",
        "Registration received and approval is pending. You will receive an email upon approval"
      );
      commit("setRegistrationSuccess", true);
      commit("setAuthLoading", false);

      return tempMember;
    } catch (error) {
      console.error("Error creating member:", error);
      commit("setError", error.message);
      throw error;
    }
  },

  async createCompany({ dispatch, getters }, payload) {
    if (!payload || !payload.title) {
      throw new Error("Company data with title is required");
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const companiesRef = collection(programRef, "companies");

      const titleUppercase = payload.title.toUpperCase();
      const companyDataKeyLabel =
        getters.currentProgram?.companyDataKeyLabel || "identifier";

      const dupCheckRequests = [
        getDocs(
          query(companiesRef, where("titleUppercase", "==", titleUppercase))
        )
      ];

      if (payload.accountKey) {
        dupCheckRequests.push(
          getDocs(
            query(companiesRef, where("accountKey", "==", payload.accountKey))
          )
        );
      }

      const [titleDupSnapshot, accountKeyDupSnapshot] = await Promise.all(
        dupCheckRequests
      );

      const registrationErrors = [];

      if (!titleDupSnapshot.empty) {
        registrationErrors.push("That company name is already registered");
      }

      if (accountKeyDupSnapshot && !accountKeyDupSnapshot.empty) {
        registrationErrors.push(
          `That ${companyDataKeyLabel} is already registered`
        );
      }

      if (registrationErrors.length > 0) {
        throw new Error(registrationErrors.join(", "));
      }

      // eslint-disable-next-line no-unused-vars
      const { id, ...companyInput } = payload;
      const company = {
        ...companyInput,
        titleUppercase,
        created: serverTimestamp(),
        updated: serverTimestamp(),
        createdBy: getters.user?.id || "system",
        updatedBy: getters.user?.id || "system"
      };

      const newCompanyRef = await addDoc(companiesRef, company);
      return newCompanyRef;
    } catch (error) {
      console.error("Error creating company:", error);
      throw error;
    }
  },

  async updateMember({ commit, getters, dispatch }, payload) {
    if (!payload || !payload.id) {
      throw new Error("Member data with ID is required");
    }

    commit("setError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const memberRef = doc(collection(programRef, "members"), payload.id);

      // Fetch current member data
      const memberDoc = await getDoc(memberRef);
      if (!memberDoc.exists()) {
        throw new Error("Member not found");
      }

      const storedData = memberDoc.data();
      const storedMember = decryptMemberSensitiveData(storedData);

      // Check for duplicate email if email is being updated
      if (payload.email && payload.email !== storedMember.email) {
        const existingMember = await dispatch(
          "searchMemberByEmail",
          payload.email
        );
        if (existingMember && existingMember.id !== payload.id) {
          throw new Error("Email is already registered");
        }
      }

      // Check for duplicate account key if account key is being updated
      if (
        payload.accountKey &&
        payload.accountKey !== storedMember.accountKey
      ) {
        const accountKeyDupQuery = query(
          collection(programRef, "members"),
          where("accountKey", "==", payload.accountKey)
        );
        const accountKeyDupSnapshot = await getDocs(accountKeyDupQuery);

        if (!accountKeyDupSnapshot.empty) {
          let duplicated = false;
          accountKeyDupSnapshot.forEach(docSnapshot => {
            if (docSnapshot.id !== payload.id) {
              duplicated = true;
            }
          });
          if (duplicated) {
            throw new Error("Account Key is already registered");
          }
        }
      }

      // eslint-disable-next-line no-unused-vars
      const { id, ...memberInput } = payload;

      // Update fullnameUppercase if firstname or lastname changed
      const updates = { ...memberInput };
      if (payload.firstname || payload.lastname) {
        const firstname = payload.firstname || storedMember.firstname;
        const lastname = payload.lastname || storedMember.lastname;
        updates.fullnameUppercase = `${firstname} ${lastname}`.toUpperCase();
        // Also update the unencrypted search field
        updates.firstnameUppercase = firstname.toUpperCase();
      }

      // Encrypt sensitive data before saving
      const encryptedUpdates = encryptMemberSensitiveData(updates);

      const memberUpdates = {
        ...encryptedUpdates,
        updated: serverTimestamp(),
        updatedBy: getters.currentMember?.id || "system"
      };

      await updateDoc(memberRef, memberUpdates);

      // Update local state with decrypted data
      const updatedMember = {
        ...storedMember,
        ...payload,
        updated: new Date(),
        updatedBy: getters.currentMember?.id || "system"
      };

      commit("updateMember", updatedMember);
      return updatedMember;
    } catch (error) {
      console.error("Error updating member:", error);
      commit("setError", error.message);
      throw error;
    }
  },

  async deleteMember({ dispatch, commit, getters }, memberId) {
    if (!memberId) {
      throw new Error("Member ID is required");
    }

    commit("setError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const membersRef = collection(programRef, "members");
      const memberRef = doc(membersRef, memberId);

      // Fetch member data to get company associations
      const memberDoc = await getDoc(memberRef);
      if (!memberDoc.exists()) {
        throw new Error("Member not found");
      }

      const storedMember = memberDoc.data();
      const storedCompanies = storedMember.companies || [];

      // Prepare company changes for removal
      const companyChanges = storedCompanies.map(companyId => ({
        companyId,
        memberId,
        type: "remove"
      }));

      // Delete the member
      await deleteDoc(memberRef);

      // Update company memberships
      if (companyChanges.length > 0) {
        try {
          await dispatch("syncCompanyMembers", companyChanges);
        } catch (error) {
          console.error("Error updating company members:", error);
          // Don't throw here as member is already deleted
        }
      }

      commit("deleteMember", memberId);
      dispatch("setSnackbar", "Member deleted successfully");
      return true;
    } catch (error) {
      console.error("Error deleting member:", error);
      commit("setError", error.message);
      throw error;
    }
  },

  async syncCompanyMembers({ getters }, payload) {
    if (!payload || !Array.isArray(payload) || payload.length === 0) {
      return;
    }

    try {
      const batch = writeBatch(db());

      payload.forEach(({ companyId, memberId, type }) => {
        const programRef = doc(db(), "programs", getters.programId);
        const updateCompanyRef = doc(
          collection(programRef, "companies"),
          companyId
        );

        const update = {
          updated: serverTimestamp(),
          updatedBy: getters.user?.id || "system"
        };

        if (type === "add") {
          update.members = arrayUnion(memberId);
        } else if (type === "remove") {
          update.members = arrayRemove(memberId);
        }

        batch.update(updateCompanyRef, update);
      });

      await batch.commit();
    } catch (error) {
      console.error("Error syncing company members:", error);
      throw new Error("Error occurred when updating company members");
    }
  },

  async syncMembersToCompanies({ commit, getters }) {
    commit("setLoading", true);
    commit("setError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const membersRef = collection(programRef, "members");
      const membersQuery = query(membersRef, orderBy("fullnameUppercase"));

      const membersSnapshot = await getDocs(membersQuery);
      const members = [];
      membersSnapshot.forEach(docSnapshot => {
        members.push({
          id: docSnapshot.id,
          ...docSnapshot.data()
        });
      });

      const companiesRef = collection(programRef, "companies");
      const companiesSnapshot = await getDocs(companiesRef);
      const companies = [];
      companiesSnapshot.forEach(docSnapshot => {
        companies.push({
          id: docSnapshot.id,
          ...docSnapshot.data()
        });
      });

      const batch = writeBatch(db());

      companies.forEach(company => {
        const memberIds = [];
        members.forEach(member => {
          if (member.companies && member.companies.includes(company.id)) {
            memberIds.push(member.id);
          }
        });

        const companyRef = doc(companiesRef, company.id);
        batch.update(companyRef, {
          members: memberIds,
          updated: serverTimestamp(),
          updatedBy: getters.user?.id || "system"
        });
      });

      await batch.commit();
    } catch (error) {
      console.error("Error syncing members to companies:", error);
      commit("setError", error.message);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  async inviteByStatus({ getters, dispatch }, status) {
    if (!status) {
      throw new Error("Status is required");
    }

    const members = getters.members.filter(member => member.status === status);

    for (const member of members) {
      try {
        await dispatch("inviteMember", member.id);
      } catch (error) {
        console.error(`Error inviting member ${member.id}:`, error);
        // Continue with other members
      }
    }
  },

  async inviteMember({ getters, commit, dispatch }, memberId) {
    if (!memberId) {
      throw new Error("Member ID is required");
    }

    commit("setLoading", true);
    commit("setError", null);

    try {
      await webApi.post("/inviteMember", {
        memberId: memberId,
        programId: getters.programId
      });
      dispatch("setSnackbar", "Invitation sent successfully");
    } catch (error) {
      console.error("Error sending invitation:", error);
      commit("setError", error.message);
      throw new Error("Error occurred when sending invitation");
    } finally {
      commit("setLoading", false);
    }
  },

  async approveMember({ getters, commit, dispatch }, memberId) {
    if (!memberId) {
      throw new Error("Member ID is required");
    }

    commit("setLoading", true);
    commit("setError", null);

    try {
      await webApi.post("/approveMember", {
        memberId: memberId,
        programId: getters.programId
      });
      dispatch("setSnackbar", "Member approved successfully");
    } catch (error) {
      console.error("Error approving member:", error);
      commit("setError", error.message);
      throw new Error("Error occurred when approving member");
    } finally {
      commit("setLoading", false);
    }
  },

  async setCurrentMemberPoints({ commit }, payload) {
    commit("setCurrentMemberPoints", payload);
  },

  async importMembers({ getters, commit }, importingMembers) {
    if (
      !importingMembers ||
      !Array.isArray(importingMembers) ||
      importingMembers.length === 0
    ) {
      throw new Error("Members array is required");
    }

    commit("setImportProgress", {
      importing: true,
      progress: 0,
      total: importingMembers.length,
      processed: 0
    });
    commit("setError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const membersCollection = collection(programRef, "members");

      const batchSize = 500;
      const totalMembers = importingMembers.length;

      for (let i = 0; i < totalMembers; i += batchSize) {
        const membersBatch = writeBatch(db());
        const batchMembers = importingMembers.slice(i, i + batchSize);

        batchMembers.forEach(member => {
          const newMemberRef = doc(membersCollection);
          const dataWithTimestamp = {
            ...member,
            created: serverTimestamp(),
            updated: serverTimestamp(),
            createdBy: getters.user?.id || "system",
            updatedBy: getters.user?.id || "system"
          };
          membersBatch.set(newMemberRef, dataWithTimestamp);
        });

        await membersBatch.commit();

        const processed = Math.min(i + batchSize, totalMembers);
        const progress = (processed / totalMembers) * 100;
        commit("setImportProgress", {
          importing: true,
          progress,
          total: totalMembers,
          processed
        });
      }

      commit("setImportProgress", {
        importing: false,
        progress: 100,
        total: totalMembers,
        processed: totalMembers
      });
      return totalMembers;
    } catch (error) {
      console.error("Error importing members:", error);
      commit("setError", error.message);
      commit("setImportProgress", {
        importing: false,
        progress: 0,
        total: 0,
        processed: 0
      });
      throw error;
    }
  },

  async importMembersNew({ getters, commit }, newMembers) {
    if (!newMembers || !Array.isArray(newMembers) || newMembers.length === 0) {
      throw new Error("Members array is required");
    }

    commit("setImportProgress", {
      importing: true,
      progress: 0,
      total: newMembers.length,
      processed: 0
    });
    commit("setError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const batchSize = 500;
      const totalMembers = newMembers.length;

      for (let i = 0; i < totalMembers; i += batchSize) {
        const newMembersBatch = writeBatch(db());
        const batchMembers = newMembers.slice(i, i + batchSize);

        batchMembers.forEach(member => {
          const exMemberId = member.id;
          delete member.id;

          const updateMemberRef = doc(
            collection(programRef, "members"),
            exMemberId
          );
          const dataWithTimestamp = {
            ...member,
            updated: serverTimestamp(),
            updatedBy: getters.user?.id || "system"
          };
          newMembersBatch.update(updateMemberRef, dataWithTimestamp);
        });

        await newMembersBatch.commit();

        const processed = Math.min(i + batchSize, totalMembers);
        const progress = (processed / totalMembers) * 100;
        commit("setImportProgress", {
          importing: true,
          progress,
          total: totalMembers,
          processed
        });
      }

      commit("setImportProgress", {
        importing: false,
        progress: 100,
        total: totalMembers,
        processed: totalMembers
      });
      return totalMembers;
    } catch (error) {
      console.error("Error importing members (update):", error);
      commit("setError", error.message);
      commit("setImportProgress", {
        importing: false,
        progress: 0,
        total: 0,
        processed: 0
      });
      throw error;
    }
  },

  async searchMembers({ getters, commit }, searchTerm) {
    if (!searchTerm) {
      return [];
    }

    commit("setError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const membersRef = collection(programRef, "members");

      // Search by email (exact match)
      const emailQuery = query(
        membersRef,
        where("email", "==", searchTerm.toLowerCase())
      );
      const emailSnapshot = await getDocs(emailQuery);

      const results = [];
      emailSnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        results.push({
          id: docSnapshot.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        });
      });

      return results;
    } catch (error) {
      console.error("Error searching members:", error);
      commit("setError", error.message);
      return [];
    }
  },

  async searchMemberByEmail({ getters, commit }, email) {
    if (!email) {
      return null;
    }

    commit("setError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const membersRef = collection(programRef, "members");

      // First try searching with encrypted email
      const encryptedEmail = encryptionService.encrypt(email.toLowerCase());
      const encryptedQuery = query(
        membersRef,
        where("email", "==", encryptedEmail),
        limit(1)
      );

      let querySnapshot = await getDocs(encryptedQuery);

      // If no results with encrypted search, try unencrypted (for backwards compatibility)
      if (querySnapshot.empty) {
        const unencryptedQuery = query(
          membersRef,
          where("email", "==", email.toLowerCase()),
          limit(1)
        );
        querySnapshot = await getDocs(unencryptedQuery);
      }

      if (!querySnapshot.empty) {
        const memberDoc = querySnapshot.docs[0];
        const data = memberDoc.data();
        const decryptedData = decryptMemberSensitiveData(data);

        return {
          id: memberDoc.id,
          ...decryptedData,
          fullname: `${decryptedData.firstname ||
            ""} ${decryptedData.lastname || ""}`.trim(),
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        };
      }

      return null;
    } catch (error) {
      console.error("Error searching member by email:", error);
      commit("setError", error.message);
      return null;
    }
  },

  async signInAnonymously({ commit }) {
    try {
      await signInAnonymously(auth);
      return true;
    } catch (error) {
      console.error("Error signing in anonymously:", error);
      commit("setError", error.message);
      throw error;
    }
  },

  clearError({ commit }) {
    commit("setError", null);
  },

  clearCurrentMember({ commit }) {
    commit("setCurrentMember", null);
  },

  clearMembers({ commit }) {
    commit("setMembers", []);
    commit("setLastVisible", null);
    commit("setHasMore", true);
  }
};

const mutations = {
  setMembers(state, payload) {
    state.members = payload;
  },

  addMembers(state, payload) {
    state.members = [...state.members, ...payload];
  },

  setCurrentMember(state, payload) {
    state.currentMember = payload;
  },

  setLoadingCurrentMember(state, payload) {
    state.loadingCurrentMember = Boolean(payload);
  },

  setCurrentMemberPoints(state, payload) {
    state.currentMemberPoints = payload;
  },

  setLoadingMembers(state, payload) {
    state.loadingMembers = Boolean(payload);
  },

  setError(state, payload) {
    state.error = payload;
  },

  setImportProgress(state, payload) {
    state.importProgress = { ...payload };
  },

  createMember(state, payload) {
    state.members = [...state.members, payload];
  },

  updateMember(state, payload) {
    const index = state.members.findIndex(m => m.id === payload.id);
    if (index !== -1) {
      state.members.splice(index, 1, { ...state.members[index], ...payload });
    }

    if (state.currentMember && state.currentMember.id === payload.id) {
      state.currentMember = { ...state.currentMember, ...payload };
    }

    if (state.memberDetails[payload.id]) {
      state.memberDetails[payload.id] = {
        ...state.memberDetails[payload.id],
        ...payload
      };
    }
  },

  patchMember(state, payload) {
    const index = state.members.findIndex(item => item.id === payload.id);
    if (index !== -1) {
      state.members[index] = { ...state.members[index], ...payload };
    }
  },

  deleteMember(state, payload) {
    state.members = state.members.filter(item => item.id !== payload);
  },

  setLastVisible(state, payload) {
    state.lastVisible = payload;
  },

  setHasMore(state, payload) {
    state.hasMore = Boolean(payload);
  }
};

const getters = {
  members(state) {
    return state.members;
  },

  currentMember(state) {
    return state.currentMember;
  },

  loadingCurrentMember(state) {
    return state.loadingCurrentMember;
  },

  currentMemberPoints(state) {
    return state.currentMemberPoints;
  },

  loadingMembers(state) {
    return state.loadingMembers;
  },

  memberError(state) {
    return state.error;
  },

  importProgress(state) {
    return state.importProgress;
  },

  membersMap(state) {
    return state.members.reduce((map, member) => {
      map[member.id] = member;
      return map;
    }, {});
  },

  memberAccountKeysMap(state) {
    return state.members.reduce((map, member) => {
      if (member.accountKey) {
        map[member.accountKey] = member;
      }
      return map;
    }, {});
  },

  getMemberById: state => id => {
    return state.members.find(member => member.id === id) || null;
  },

  getMemberByEmail: state => email => {
    return (
      state.members.find(
        member => member.email?.toLowerCase() === email?.toLowerCase()
      ) || null
    );
  },

  getMemberByAccountKey: state => accountKey => {
    return (
      state.members.find(member => member.accountKey === accountKey) || null
    );
  },

  membersByStatus: state => status => {
    return state.members.filter(member => member.status === status);
  },

  membersByCompany: state => companyId => {
    return state.members.filter(
      member => member.companies && member.companies.includes(companyId)
    );
  },

  activeMembersCount(state) {
    return state.members.filter(member => member.status === "Active").length;
  },

  pendingMembersCount(state) {
    return state.members.filter(member => member.status === "Awaiting Approval")
      .length;
  },

  membersCount(state) {
    return state.members.length;
  },

  totalMemberBalance(state) {
    return state.members.reduce(
      (total, member) => total + (member.balance || 0),
      0
    );
  },

  totalMemberAwarded(state) {
    return state.members.reduce(
      (total, member) => total + (member.awarded || 0),
      0
    );
  },

  totalMemberRedeemed(state) {
    return state.members.reduce(
      (total, member) => total + (member.redeemed || 0),
      0
    );
  },

  hasMembers(state) {
    return state.members.length > 0;
  },

  membersOptions(state) {
    return state.members.map(member => ({
      text: member.fullname,
      value: member.id
    }));
  },

  getMemberDetails: state => memberId => {
    return state.memberDetails[memberId] || null;
  },

  lastVisible(state) {
    return state.lastVisible;
  },

  hasMore(state) {
    return state.hasMore;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
